import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getApiBase } from './apiBase';
import { getMyArchivePersonId } from './myArchivePerson';
import { storybookApi } from './storybook';
import { remindersApi } from './reminders';
import { hasGuideExportPreviewed, hasGuideMemorialExplored } from './guideProgress';

const API = getApiBase();

/**
 * Aggregate real archive data for the “Build your archive” checklist.
 */
export async function fetchBuildArchiveProgress({ trees = [], user = null } = {}) {
  const progress = {
    hasSelf: Boolean(user?.self_person_id || getMyArchivePersonId()),
    peopleCount: 0,
    storyCount: 0,
    documentCount: 0,
    possessionCount: 0,
    hasStorybook: false,
    reminderCount: 0,
    hasMemorialWorkspace: false,
    exportPreviewed: hasGuideExportPreviewed(),
    memorialExplored: hasGuideMemorialExplored(),
  };

  for (const tree of trees.slice(0, 5)) {
    try {
      const { data: people } = await axios.get(`${API}/individuals/${tree.id}`);
      const rows = Array.isArray(people) ? people : [];
      progress.peopleCount += rows.length;
      for (const person of rows.slice(0, 15)) {
        if (person.memorial_mode_enabled) progress.hasMemorialWorkspace = true;
        try {
          const [storiesRes, docsRes] = await Promise.all([
            axios.get(`${API}/individuals/${person.id}/stories`),
            axios.get(`${API}/documents/${person.id}`),
          ]);
          progress.storyCount += (storiesRes.data || []).length;
          progress.documentCount += (docsRes.data || []).length;
        } catch {
          /* ignore per-person */
        }
      }
    } catch {
      /* ignore per-tree */
    }
    try {
      const { data: markers } = await axios.get(`${API}/trees/${tree.id}/memory-markers`);
      const list = Array.isArray(markers) ? markers : [];
      progress.possessionCount += list.filter((m) =>
        ['object', 'keepsake'].includes(m.marker_type)
      ).length;
    } catch {
      /* optional endpoint */
    }
  }

  try {
    const books = await storybookApi.list({});
    progress.hasStorybook = Array.isArray(books) && books.some((b) => (b.completed_count || 0) > 0 || b.status === 'in_progress');
    if (!progress.hasStorybook && Array.isArray(books) && books.length > 0) {
      progress.hasStorybook = true; // started
    }
  } catch {
    /* ignore */
  }

  try {
    const reminders = await remindersApi.list();
    progress.reminderCount = (reminders || []).length;
  } catch {
    /* ignore */
  }

  return progress;
}

export function useBuildArchiveProgress(trees, user) {
  const { token, authReady } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authReady || !token) {
      setProgress(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      setLoading(true);
      fetchBuildArchiveProgress({ trees, user })
        .then((p) => {
          if (!cancelled) setProgress(p);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [trees, user, token, authReady]);

  return { progress, loading, refresh: () => fetchBuildArchiveProgress({ trees, user }).then(setProgress) };
}
